// skipcq: JS-W1028
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import TapRatingBar from '../ratings/TapRatingBar';
import { DETAIL_MUTED, PRIMARY_DETAIL_TEXT } from '../../utils/theme';
import {
  getTopBeerTagLabels,
  resolveBeerStyleLabel,
} from '../../utils/brewhouseBeerStats';
import BeerTagChips from './BeerTagChips';

const rowGridSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  columnGap: 1.5,
  alignItems: 'start',
};

const truncatedTextSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/**
 * @param {object} props
 * @param {object} props.beer
 * @param {Record<string, string>} props.tagLabelMap
 * @param {Record<string, string>} props.styleLabelMap
 * @param {string} [props.breweryId]
 * @param {'recently-added' | 'top-rated'} [props.section]
 * @param {(params: { breweryId: string, beerId: string, section: 'recently-added' | 'top-rated' }) => void} [props.onRowClick]
 */
function BeerReviewStatsRow({
  beer,
  tagLabelMap,
  styleLabelMap,
  breweryId,
  section,
  onRowClick,
}) {
  const beerName = typeof beer.name === 'string' && beer.name.length > 0 ? beer.name : 'Unknown';
  const beerStyle = resolveBeerStyleLabel(beer.style, styleLabelMap);
  const tagEntries = getTopBeerTagLabels(beer.tags, tagLabelMap);
  const beerId = typeof beer.id === 'string' ? beer.id : '';
  const profilePath =
    beerId.length > 0 ? `/brewery-portal/pint-portfolio/${beerId}` : null;

  const handleRowClick = useCallback(() => {
    if (!breweryId || !section || typeof onRowClick !== 'function' || beerId.length === 0) {
      return;
    }
    onRowClick({ breweryId, beerId, section });
  }, [breweryId, section, onRowClick, beerId]);

  const handleRowKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      handleRowClick();
    },
    [handleRowClick],
  );

  const rowContent = (
    <Box sx={rowGridSx}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ ...truncatedTextSx, color: PRIMARY_DETAIL_TEXT }}
        >
          {beerName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ ...truncatedTextSx, color: DETAIL_MUTED, mt: 0.25 }}
        >
          {beerStyle}
        </Typography>
      </Box>
      <Box sx={{ justifySelf: 'end', pt: 0.25 }}>
        <TapRatingBar
          rating={beer.ratings?.overall}
          size="compact"
          showNumeric
        />
      </Box>
      <Box sx={{ gridColumn: '1 / -1' }}>
        <BeerTagChips tags={tagEntries} />
      </Box>
    </Box>
  );

  if (!profilePath) {
    return <Box sx={{ py: 1 }}>{rowContent}</Box>;
  }

  return (
    <Box
      component={RouterLink}
      to={profilePath}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      aria-label={`View details for ${beerName}`}
      sx={{
        display: 'block',
        py: 1,
        color: 'inherit',
        textDecoration: 'none',
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {rowContent}
    </Box>
  );
}

BeerReviewStatsRow.propTypes = {
  beer: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    style: PropTypes.string,
    ratings: PropTypes.shape({
      overall: PropTypes.number,
    }),
    tags: PropTypes.object,
  }).isRequired,
  tagLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  styleLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  breweryId: PropTypes.string,
  section: PropTypes.oneOf(['recently-added', 'top-rated']),
  onRowClick: PropTypes.func,
};

export default BeerReviewStatsRow;
